import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  canHoldVisits,
  emptyLine,
  type LineDraft,
  type LineItemKind,
  type Role,
  type QuotePreviewInput,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { Field, field } from '../create/Field';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { IconPlus, IconTrash } from '../icons';
import { useDebounced } from '../ui/useDebounced';
import { money } from '../money';
import { AvailabilitySheet, type AvailabilityContext } from '../jobs/AvailabilitySheet';
import { useOrgClock } from '../../lib/useOrgClock';
import { quoteWindowInput, quoteWindowInstant } from './QuotePlanningFields';

/** A quote records proposed resources. Only office confirmation reserves stock. */
export function QuoteLineGrid({
  lines,
  onChange,
  disabled = false,
  onStockAvailability,
  onCrewAvailability,
}: {
  lines: LineDraft[];
  onChange: (next: LineDraft[]) => void;
  disabled?: boolean;
  onStockAvailability?: (line: LineDraft) => void;
  onCrewAvailability?: (line: LineDraft) => void;
}) {
  const trpc = useTRPC();
  const [availability, setAvailability] = useState<
    (AvailabilityContext & { lineIndex?: number }) | null
  >(null);
  const clock = useOrgClock();
  const catalog = useQuery(trpc.priceBook.list.queryOptions());
  const locations = useQuery(trpc.inventory.locations.queryOptions());
  const members = useQuery(trpc.org.members.queryOptions());
  const patch = (index: number, changes: Partial<LineDraft>) =>
    onChange(lines.map((line, i) => (i === index ? { ...line, ...changes } : line)));
  return (
    <section className="space-y-3" aria-label="Quote line items">
      <div>
        <h3 className="text-sm font-semibold">Line items</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Choose items, proposed sources and responsibilities. Quoted quantities do not
          reserve stock, and proposed assignments do not hold a crew's calendar.
        </p>
      </div>
      {catalog.isError ||
      locations.isError ||
      members.isError ||
      clock.state === 'error' ||
      clock.state === 'offline' ? (
        <Notice tone="warn">
          Some resource choices could not refresh. Your entered lines are retained.
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              void catalog.refetch();
              void locations.refetch();
              void members.refetch();
              clock.refetch();
            }}
          >
            Refresh choices
          </Button>
        </Notice>
      ) : null}
      {lines.map((line, index) => (
        <fieldset
          key={index}
          disabled={disabled}
          className="min-w-0 rounded-[var(--radius)] border border-border bg-surface p-3"
        >
          <legend className="px-1 text-xs font-semibold">Line {index + 1}</legend>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(180px,2fr)_minmax(180px,2fr)_100px_80px_90px_110px]">
            <Field label="Catalog item">
              <select
                className={field}
                value={line.priceBookItemId ?? ''}
                disabled={disabled || catalog.isPending || catalog.isError}
                onChange={(event) => {
                  const item = catalog.data?.find(
                    (candidate) => candidate.id === event.target.value,
                  );
                  patch(
                    index,
                    item
                      ? {
                          priceBookItemId: item.id,
                          name: item.name,
                          kind: item.kind as LineItemKind,
                          price: (item.unitPriceCents / 100).toFixed(2),
                          unitLabel: item.unitLabel ?? undefined,
                          description: item.description ?? undefined,
                          taxable: item.taxable,
                          ...(item.kind !== 'service' && item.kind !== 'labor'
                            ? { requestedStartAt: undefined, requestedEndAt: undefined }
                            : {}),
                        }
                      : { priceBookItemId: undefined, sourceLocationId: undefined },
                  );
                }}
              >
                <option value="">Custom line</option>
                {line.priceBookItemId &&
                !catalog.data?.some((item) => item.id === line.priceBookItemId) ? (
                  <option value={line.priceBookItemId}>
                    Previously selected item · unavailable
                  </option>
                ) : null}
                {(catalog.data ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Line description">
              <input
                className={field}
                value={line.name}
                maxLength={200}
                onChange={(event) => patch(index, { name: event.target.value })}
              />
            </Field>
            <Field label="Kind">
              <select
                className={field}
                value={line.kind}
                onChange={(event) =>
                  patch(index, {
                    kind: event.target.value as LineItemKind,
                    ...(event.target.value !== 'service' && event.target.value !== 'labor'
                      ? { requestedStartAt: undefined, requestedEndAt: undefined }
                      : {}),
                  })
                }
              >
                <option value="service">Service</option>
                <option value="labor">Labor</option>
                <option value="material">Material</option>
                <option value="fee">Fee</option>
              </select>
            </Field>
            <Field label="Quantity">
              <input
                className={field}
                inputMode="decimal"
                value={line.qty}
                onChange={(event) => patch(index, { qty: event.target.value })}
              />
            </Field>
            <Field label="Unit">
              <input
                className={field}
                value={line.unitLabel ?? ''}
                maxLength={20}
                placeholder="Each"
                onChange={(event) =>
                  patch(index, { unitLabel: event.target.value || undefined })
                }
              />
            </Field>
            <Field label="Unit price">
              <input
                className={field}
                inputMode="decimal"
                value={line.price}
                placeholder="0.00"
                onChange={(event) => patch(index, { price: event.target.value })}
              />
            </Field>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Field label="Proposed stock location">
              <select
                className={field}
                value={line.sourceLocationId ?? ''}
                disabled={
                  disabled ||
                  !line.priceBookItemId ||
                  locations.isPending ||
                  locations.isError
                }
                onChange={(event) =>
                  patch(index, { sourceLocationId: event.target.value || undefined })
                }
              >
                <option value="">Choose proposed source</option>
                {line.sourceLocationId &&
                !locations.data?.some(
                  (location) => location.id === line.sourceLocationId,
                ) ? (
                  <option value={line.sourceLocationId}>
                    Previously selected location · unavailable
                  </option>
                ) : null}
                {(locations.data ?? []).map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Tracked materials need a source and task before saving. The quote creates
                no reservation.
              </p>
            </Field>
            <Field label="Task">
              <input
                className={field}
                value={line.taskName ?? ''}
                maxLength={200}
                placeholder="e.g. Install fence"
                onChange={(event) =>
                  patch(index, { taskName: event.target.value || undefined })
                }
              />
            </Field>
            <Field label="Proposed assignee">
              <select
                className={field}
                value={line.assignedUserId ?? ''}
                disabled={disabled || members.isPending || members.isError}
                onChange={(event) =>
                  patch(index, { assignedUserId: event.target.value || undefined })
                }
              >
                <option value="">Unassigned</option>
                {line.assignedUserId &&
                !members.data?.some((member) => member.userId === line.assignedUserId) ? (
                  <option value={line.assignedUserId}>
                    Previously selected member · unavailable
                  </option>
                ) : null}
                {(members.data ?? [])
                  .filter((member) => canHoldVisits(member.role as Role))
                  .map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Estimated minutes">
              <input
                className={field}
                type="number"
                min={1}
                step={1}
                value={line.estimatedDurationMinutes ?? ''}
                onChange={(event) =>
                  patch(index, {
                    estimatedDurationMinutes: event.target.value
                      ? Number(event.target.value)
                      : undefined,
                  })
                }
              />
            </Field>
          </div>
          {line.kind === 'service' || line.kind === 'labor' ? (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label={`Requested start${clock.tz ? ` · ${clock.tz}` : ''}`}>
                <input
                  className={field}
                  type="datetime-local"
                  disabled={disabled || !clock.tz}
                  value={
                    clock.tz ? quoteWindowInput(line.requestedStartAt, clock.tz) : ''
                  }
                  onChange={(event) => {
                    if (clock.tz)
                      patch(index, {
                        requestedStartAt: quoteWindowInstant(
                          event.target.value,
                          clock.tz,
                        ),
                      });
                  }}
                />
              </Field>
              <Field label="Requested end">
                <input
                  className={field}
                  type="datetime-local"
                  disabled={disabled || !clock.tz}
                  value={clock.tz ? quoteWindowInput(line.requestedEndAt, clock.tz) : ''}
                  onChange={(event) => {
                    if (clock.tz)
                      patch(index, {
                        requestedEndAt: quoteWindowInstant(event.target.value, clock.tz),
                      });
                  }}
                />
              </Field>
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label className="mr-auto flex min-h-10 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={line.taxable !== false}
                onChange={(event) => patch(index, { taxable: event.target.checked })}
              />{' '}
              Taxable
            </label>
            {line.priceBookItemId ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  onStockAvailability
                    ? onStockAvailability(line)
                    : setAvailability({
                        kind: 'item',
                        itemId: line.priceBookItemId!,
                        name: line.name,
                        locationId: line.sourceLocationId,
                      })
                }
              >
                Stock availability
              </Button>
            ) : null}
            {line.kind === 'labor' || line.kind === 'service' ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  onCrewAvailability
                    ? onCrewAvailability(line)
                    : setAvailability({
                        kind: 'crew',
                        name: line.name,
                        userId: line.assignedUserId,
                        durationMinutes: line.estimatedDurationMinutes,
                        startsAt: line.requestedStartAt,
                        endsAt: line.requestedEndAt,
                        lineIndex: index,
                      })
                }
              >
                Crew availability
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Remove line ${index + 1}`}
              onClick={() => onChange(lines.filter((_, i) => i !== index))}
            >
              <IconTrash size={14} /> Remove
            </Button>
          </div>
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer py-2 font-medium">
              Additional line details
            </summary>
            <textarea
              className={`${field} mt-1 min-h-20 w-full py-2`}
              aria-label={`Line ${index + 1} additional details`}
              value={line.description ?? ''}
              maxLength={2000}
              onChange={(event) =>
                patch(index, { description: event.target.value || undefined })
              }
            />
          </details>
        </fieldset>
      ))}
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled || lines.length >= 200}
        onClick={() => onChange([...lines, emptyLine()])}
      >
        <IconPlus size={14} /> Add line
      </Button>
      {availability ? (
        <AvailabilitySheet
          context={availability}
          onClose={() => setAvailability(null)}
          onApplyCrew={
            availability.kind === 'crew' && availability.lineIndex !== undefined
              ? (candidate) => {
                  if (disabled || availability.lineIndex === undefined) return;
                  patch(availability.lineIndex, {
                    assignedUserId: candidate.userId,
                    requestedStartAt: candidate.startsAt.toISOString(),
                    requestedEndAt: candidate.endsAt.toISOString(),
                  });
                  setAvailability(null);
                }
              : undefined
          }
        />
      ) : null}
    </section>
  );
}

/** Pricing and tax are returned by the same server rules as the saved document. */
export function QuoteTotalsPreview(input: QuotePreviewInput) {
  const trpc = useTRPC();
  const serialized = JSON.stringify(input);
  const settled = useDebounced(serialized);
  const previewInput = JSON.parse(settled) as QuotePreviewInput;
  const preview = useQuery({
    ...trpc.quote.preview.queryOptions(previewInput),
    enabled: previewInput.lines.length > 0,
  });
  if (input.lines.length === 0)
    return (
      <p className="text-xs text-muted-foreground">
        Complete a line to calculate document totals.
      </p>
    );
  if (
    serialized !== settled ||
    (!preview.isPaused && !preview.isError && (preview.isPending || preview.isFetching))
  )
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Calculating document totals…
      </p>
    );
  if (preview.isError || preview.isPaused)
    return (
      <Notice tone="warn">
        Document totals are unavailable. Your entered lines are retained.
        <Button variant="ghost" size="sm" onClick={() => void preview.refetch()}>
          Retry totals
        </Button>
      </Notice>
    );
  if (!preview.data) return null;
  return (
    <dl
      className="ml-auto max-w-xs space-y-2 rounded-[var(--radius)] bg-surface-2 p-3 text-sm"
      aria-label="Server calculated quote totals"
    >
      <div className="flex justify-between gap-4">
        <dt>Subtotal</dt>
        <dd className="tabular-nums">{money(preview.data.subtotalCents)}</dd>
      </div>
      <div className="flex justify-between gap-4">
        <dt>Tax</dt>
        <dd className="tabular-nums">{money(preview.data.taxCents)}</dd>
      </div>
      <div className="flex justify-between gap-4 border-t border-border pt-2 font-semibold">
        <dt>Total</dt>
        <dd className="tabular-nums">{money(preview.data.totalCents)}</dd>
      </div>
    </dl>
  );
}
